// For Hospitals & Institutes public partnership page + Super Admin viewer.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, GraduationCap, Users, Award, BarChart3, FileSpreadsheet,
  Palette, Sparkles, PhoneCall, Rocket, Building2, MessageCircle, ArrowRight,
  Check, ChevronDown, ChevronUp, Send, Home, ShoppingCart, X,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { usePageTitle } from "@/hooks/usePageTitle";
import { StoreHeader } from "@/pages/storefront/StorefrontPages";
import { api, formatApiError } from "@/lib/api";
import { useWhatsAppUrl } from "@/context/SiteConfigContext";

// ============================================================
// PUBLIC — For Hospitals Page
// ============================================================

const ORG_TYPES = [
  "Hospital", "Medical College", "Nursing Institute", "Diagnostic Center",
  "Healthcare Training Institute", "Corporate Healthcare Organization",
  "Consultant / Referral Partner", "Other",
];

const INTERESTS = [
  "Staff Training Programs", "Infection Control Course", "NABH / Quality Training",
  "Nursing Training", "White Label LMS", "Bulk Staff Enrollment",
  "Certificate Programs", "Corporate Training", "Referral / Channel Partnership",
  "Request Demo", "Other",
];

const TIMELINES = [
  "Immediately", "Within 15 Days", "Within 1 Month", "Within 3 Months", "Not Sure",
];

const BENEFITS = [
  { icon: GraduationCap, title: "Digital Staff Training", body: "Structured online healthcare courses for every role." },
  { icon: Users, title: "Department-wise Assignment", body: "Assign courses to Nursing, ICU, OT, Lab, Quality, HR and more." },
  { icon: FileSpreadsheet, title: "Online Exams & Assessments", body: "MCQ exams with random paper generation and reattempts." },
  { icon: Award, title: "Certificate Management", body: "QR-verified certificates with expiry & renewal tracking." },
  { icon: BarChart3, title: "Compliance Dashboard", body: "Track mandatory training completion by department." },
  { icon: ShieldCheck, title: "Audit-Ready Reports", body: "Training matrix, competency matrix, certificate register." },
  { icon: FileSpreadsheet, title: "Bulk Staff Enrollment", body: "Upload staff via Excel and assign programs in bulk." },
  { icon: Palette, title: "White Label LMS", body: "Your own branded portal, logo, colors and certificate branding." },
  { icon: Sparkles, title: "AI Learning Support", body: "AI study assistant, notes generator and weak-topic detection." },
  { icon: PhoneCall, title: "Dedicated Support", body: "Direct access to Steward Healthcare quality experts." },
];

const MODELS = [
  { title: "Hospital Training Partnership", body: "For hospitals training staff on IPC, quality, NABH, patient safety, fire safety, BMW and other mandatory topics." },
  { title: "Medical College Partnership", body: "Structured programs for MBBS students, interns, residents, nursing staff and administration." },
  { title: "Nursing Institute Partnership", body: "Certificate-based programs for nursing students and practicing nurses." },
  { title: "White Label LMS Partnership", body: "Your own branded LMS portal powered by Steward Learning Cloud." },
  { title: "Corporate Healthcare Partnership", body: "Scalable training for diagnostic chains, home-care, healthcare startups." },
  { title: "Referral / Channel Partner", body: "For consultants and institutions who refer hospitals or students to Steward." },
];

const COURSES_FOR_HOSPITALS = [
  "Certificate Program in Hospital Infection Prevention & Control",
  "Certificate in Critical Care Nurse",
  "Certificate in Hospital Quality & Accreditation Program",
  "NABH Awareness Training",
  "Fire Safety Training",
  "Biomedical Waste Management Training",
  "Patient Safety and Quality Improvement",
  "Emergency Codes Training",
  "Medication Safety Training",
  "Nursing Competency Training",
];

const FAQS = [
  { q: "Can hospitals enroll multiple staff members?", a: "Yes — hospitals can enroll staff individually or through bulk Excel upload." },
  { q: "Can courses be assigned department-wise?", a: "Yes, courses can be assigned to departments, designations, or individual staff." },
  { q: "Will certificates be generated?", a: "Yes — QR-verified certificates are auto-generated on completion + exam pass, per each course's rules." },
  { q: "Can hospitals track training compliance?", a: "Yes — dashboards + reports cover completion %, exam results, certificates and renewal due dates." },
  { q: "Is white-label LMS available?", a: "Yes, eligible hospitals get a branded portal with their logo, colors, login page and certificate branding." },
  { q: "Can Steward provide custom training programs?", a: "Absolutely — we co-design custom programs based on your quality, HR and accreditation goals." },
];

export function ForHospitalsPage() {
  usePageTitle("For Hospitals & Institutes");
  const nav = useNavigate();
  const waUrl = useWhatsAppUrl();
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [f, setF] = useState({
    organization_name: "", organization_type: "", organization_size: "",
    city: "", state: "", country: "India", website: "",
    contact_person_name: "", designation: "",
    mobile: "", email: "", whatsapp_number: "",
    partnership_interests: [], approximate_learners: "",
    preferred_start_timeline: "", message: "", consent_accepted: false,
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleInterest = (i) => setF({
    ...f,
    partnership_interests: f.partnership_interests.includes(i)
      ? f.partnership_interests.filter((x) => x !== i)
      : [...f.partnership_interests, i],
  });

  const submit = async (e) => {
    e.preventDefault();
    // Client-side checks
    if (!f.organization_name.trim()) return toast.error("Organization Name is required");
    if (!f.organization_type) return toast.error("Organization Type is required");
    if (!f.contact_person_name.trim()) return toast.error("Contact Person Name is required");
    if (!f.mobile.trim() || f.mobile.replace(/\D/g, "").length < 7) return toast.error("Valid mobile is required");
    if (!f.email.trim() || !f.email.match(/^\S+@\S+\.\S+$/)) return toast.error("Valid email is required");
    if (!f.city.trim() || !f.state.trim()) return toast.error("City and State are required");
    if (!f.partnership_interests.length) return toast.error("Select at least one partnership interest");
    if (!f.consent_accepted) return toast.error("Please accept the consent to proceed");

    setBusy(true);
    try {
      await api.post("/public/partnership-enquiries", f);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50">
        <StoreHeader />
        <div className="mx-auto max-w-2xl px-4 py-24 md:px-6 text-center" data-testid="partnership-success">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#329112]/10 text-[#329112]">
            <Check className="h-8 w-8" />
          </div>
          <h1 className="mt-6 text-3xl font-black text-slate-900">Thank you!</h1>
          <p className="mt-3 text-slate-600">
            Thank you for your interest in partnering with Steward Learning Cloud.
            Our team will contact you shortly to discuss the best training and LMS solution for your organization.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/" data-testid="success-home"
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50">
              <Home className="h-4 w-4" />Back to Home
            </Link>
            <Link to="/courses" data-testid="success-courses"
                  className="inline-flex items-center gap-2 rounded-xl bg-[#101876] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0d1360]">
              <ShoppingCart className="h-4 w-4" />Explore Courses
            </Link>
            <a href={waUrl} target="_blank" rel="noreferrer" data-testid="success-wa"
               className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-700">
              <MessageCircle className="h-4 w-4" />WhatsApp Us
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="for-hospitals-page">
      <StoreHeader />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#101876] to-[#0d1360] text-white">
        <div className="mx-auto max-w-6xl px-4 py-16 md:px-6 md:py-24">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#7ce561]">Partnerships · SIHMT</div>
          <h1 className="mt-3 text-3xl font-black leading-tight sm:text-4xl md:text-5xl lg:text-6xl max-w-4xl" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Partner with Steward Institute of Healthcare Management &amp; Technology
          </h1>
          <p className="mt-5 max-w-3xl text-base leading-relaxed text-white/80 md:text-lg">
            Empower your hospital, medical college, nursing institute, or healthcare organization with
            structured digital training, certification programs, compliance dashboards, staff progress
            tracking, exams, certificates, and audit-ready reports.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#partner-form" className="inline-flex items-center gap-2 rounded-xl bg-[#329112] px-6 py-3 text-sm font-bold text-white hover:bg-[#26720d]" data-testid="hero-partner">
              <Rocket className="h-4 w-4" />Partner With Us
            </a>
            <a href="#partner-form" className="inline-flex items-center gap-2 rounded-xl border-2 border-white/40 px-6 py-3 text-sm font-bold text-white hover:bg-white/10" data-testid="hero-demo">
              Request Demo
            </a>
            <a href={waUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl border-2 border-white/40 px-6 py-3 text-sm font-bold text-white hover:bg-white/10" data-testid="hero-talk">
              <MessageCircle className="h-4 w-4" />Talk to Our Team
            </a>
          </div>
        </div>
      </section>

      {/* PARTNERSHIP MATTER */}
      <section className="border-b border-slate-200 bg-white py-16 md:py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-6 space-y-4 text-slate-700 leading-relaxed">
          <p>
            <span className="font-bold text-slate-900">Steward Learning Cloud</span> is an advanced healthcare
            learning management platform developed by Steward Healthcare India Pvt. Ltd. and Steward Institute
            of Healthcare Management &amp; Technology (SIHMT). The platform is designed to help hospitals,
            medical colleges, nursing institutes, diagnostic centers, and healthcare organizations train their
            staff through structured online courses, assessments, certificates, and compliance tracking.
          </p>
          <p>
            Our hospital partnership model enables healthcare organizations to digitally manage mandatory staff
            training, infection control programs, NABH awareness sessions, quality management courses, nursing
            competency programs, fire safety training, biomedical waste management training, emergency code
            training, and other healthcare compliance programs.
          </p>
          <p>
            Hospitals and institutes can enroll their staff, assign courses department-wise, track progress,
            conduct online exams, generate certificates, monitor renewal due dates, and download audit-ready
            reports for internal audits, NABH, NABL, ISO, JCI, and other quality requirements.
          </p>
          <p>
            Steward Learning Cloud can also support <b>white-label learning portals</b> where hospitals and
            institutes can have their own branded training dashboard, customized login page, certificate
            branding, and organization-specific training reports.
          </p>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-slate-50 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="mb-10 text-center">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#329112]">Key Benefits</div>
            <h2 className="mt-2 text-3xl font-black text-slate-900 md:text-4xl" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              What your organization gets
            </h2>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5" data-testid="benefits-grid">
            {BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.title} className="rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-lg hover:-translate-y-1 transition">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#329112]/10 text-[#329112]"><Icon className="h-5 w-5" /></div>
                  <h3 className="mt-3 text-sm font-bold text-slate-900">{b.title}</h3>
                  <p className="mt-1 text-xs text-slate-600 leading-relaxed">{b.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PARTNERSHIP MODELS */}
      <section className="border-t border-slate-200 bg-white py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="mb-10 text-center">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#329112]">Partnership Opportunities</div>
            <h2 className="mt-2 text-3xl font-black text-slate-900 md:text-4xl" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Choose a partnership that fits your goals
            </h2>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="models-grid">
            {MODELS.map((m, i) => (
              <div key={m.title} className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#101876] transition">
                <div className="text-3xl font-black text-[#329112]/25">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="mt-2 text-lg font-black text-slate-900">{m.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{m.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COURSES FOR HOSPITALS */}
      <section className="bg-slate-50 py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="mb-10 text-center">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#329112]">Sample Programs</div>
            <h2 className="mt-2 text-3xl font-black text-slate-900 md:text-4xl" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Ready-to-deploy healthcare courses
            </h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="courses-grid">
            {COURSES_FOR_HOSPITALS.map((c) => (
              <div key={c} className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#101876]/10 text-[#101876]"><GraduationCap className="h-5 w-5" /></div>
                <h3 className="mt-3 text-sm font-bold text-slate-900 line-clamp-2 min-h-[2.5rem]">{c}</h3>
                <p className="mt-2 text-xs text-slate-500 flex-1">Assign, track and certify at scale across departments.</p>
                <div className="mt-3 flex gap-2">
                  <Link to="/courses" className="flex-1 text-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50">View Details</Link>
                  <a href="#partner-form" className="flex-1 text-center rounded-lg bg-[#101876] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#0d1360]">Request Proposal</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERSHIP FORM */}
      <section id="partner-form" className="border-t border-slate-200 bg-white py-16 md:py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-6">
          <div className="mb-8 text-center">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#329112]">Become a Partner</div>
            <h2 className="mt-2 text-3xl font-black text-slate-900 md:text-4xl" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Fill in the details — we'll take it from there
            </h2>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl mx-auto">
              Our team will connect with you to discuss the best training and LMS solution for your organization.
            </p>
          </div>

          <form onSubmit={submit} className="rounded-3xl border border-slate-200 bg-white shadow-sm p-6 md:p-8 space-y-8" data-testid="partnership-form">
            {/* Organization */}
            <FormBlock title="Organization Details">
              <TextField label="Organization Name" required value={f.organization_name} onChange={set("organization_name")} testId="p-org-name" />
              <SelectField label="Organization Type" required value={f.organization_type} onChange={set("organization_type")} options={ORG_TYPES} testId="p-org-type" />
              <TextField label="Number of Beds / Students / Staff" value={f.organization_size} onChange={set("organization_size")} testId="p-org-size" />
              <TextField label="City" required value={f.city} onChange={set("city")} testId="p-city" />
              <TextField label="State" required value={f.state} onChange={set("state")} testId="p-state" />
              <TextField label="Country" value={f.country} onChange={set("country")} testId="p-country" />
              <TextField label="Website (optional)" value={f.website} onChange={set("website")} placeholder="https://" testId="p-website" />
            </FormBlock>

            {/* Contact */}
            <FormBlock title="Contact Person Details">
              <TextField label="Contact Person Name" required value={f.contact_person_name} onChange={set("contact_person_name")} testId="p-contact-name" />
              <TextField label="Designation" value={f.designation} onChange={set("designation")} testId="p-designation" />
              <TextField label="Mobile Number" required type="tel" value={f.mobile} onChange={set("mobile")} placeholder="+91 98765 43210" testId="p-mobile" />
              <TextField label="Email Address" required type="email" value={f.email} onChange={set("email")} testId="p-email" />
              <TextField label="WhatsApp Number (optional)" type="tel" value={f.whatsapp_number} onChange={set("whatsapp_number")} testId="p-whatsapp" />
            </FormBlock>

            {/* Interests */}
            <div>
              <div className="mb-3 text-sm font-black uppercase tracking-widest text-slate-500">Partnership Interest <span className="text-rose-500">*</span></div>
              <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3" data-testid="p-interests">
                {INTERESTS.map((i) => {
                  const on = f.partnership_interests.includes(i);
                  return (
                    <label key={i} className={`flex items-center gap-2 cursor-pointer rounded-lg border px-3 py-2 text-sm transition ${on ? "border-[#101876] bg-[#101876]/5 text-[#101876] font-semibold" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}
                           data-testid={`p-interest-${i.replace(/[^a-z0-9]/gi, "-")}`}>
                      <input type="checkbox" checked={on} onChange={() => toggleInterest(i)} className="h-4 w-4" />
                      {i}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Additional */}
            <FormBlock title="Additional Details">
              <TextField label="Approximate Number of Learners" value={f.approximate_learners} onChange={set("approximate_learners")} testId="p-learners" />
              <SelectField label="Preferred Start Timeline" value={f.preferred_start_timeline} onChange={set("preferred_start_timeline")} options={TIMELINES} testId="p-timeline" />
              <div className="md:col-span-2">
                <TextArea label="Message / Requirement Details" value={f.message} onChange={set("message")} testId="p-message" />
              </div>
            </FormBlock>

            {/* Consent */}
            <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 cursor-pointer" data-testid="p-consent">
              <input type="checkbox" checked={f.consent_accepted} onChange={() => setF({ ...f, consent_accepted: !f.consent_accepted })} className="mt-0.5 h-4 w-4" />
              <span className="text-sm text-slate-700">
                I agree to be contacted by Steward Healthcare India Pvt. Ltd. regarding training, LMS, certification, and partnership opportunities.
              </span>
            </label>

            <button type="submit" disabled={busy} data-testid="p-submit"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#329112] py-3.5 text-sm font-bold text-white hover:bg-[#26720d] disabled:opacity-70">
              <Send className="h-4 w-4" />{busy ? "Submitting..." : "Submit Partnership Enquiry"}
            </button>
          </form>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 py-16 md:py-20">
        <div className="mx-auto max-w-3xl px-4 md:px-6">
          <div className="mb-8 text-center">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#329112]">FAQ</div>
            <h2 className="mt-2 text-3xl font-black text-slate-900 md:text-4xl" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>Frequently asked questions</h2>
          </div>
          <div className="space-y-2" data-testid="faqs">
            {FAQS.map((f2, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className="rounded-2xl border border-slate-200 bg-white">
                  <button type="button" onClick={() => setOpenFaq(open ? null : i)}
                          className="flex w-full items-center justify-between px-5 py-4 text-left text-sm font-bold text-slate-800"
                          data-testid={`faq-${i}`}>
                    {f2.q}
                    {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  {open && <div className="border-t border-slate-100 px-5 py-4 text-sm text-slate-600">{f2.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}

// ------------ tiny form helpers ------------
function FormBlock({ title, children }) {
  return (
    <div>
      <div className="mb-3 text-sm font-black uppercase tracking-widest text-slate-500">{title}</div>
      <div className="grid gap-4 md:grid-cols-2">{children}</div>
    </div>
  );
}
function TextField({ label, required, type = "text", testId, ...rest }) {
  return (
    <div>
      <label className="block text-xs font-bold text-slate-700 mb-1">{label}{required && <span className="text-rose-500"> *</span>}</label>
      <input type={type} {...rest} data-testid={testId} required={required}
             className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-[#101876] focus:outline-none focus:ring-2 focus:ring-[#101876]/20" />
    </div>
  );
}
function SelectField({ label, required, options, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-xs font-bold text-slate-700 mb-1">{label}{required && <span className="text-rose-500"> *</span>}</label>
      <select value={value} onChange={onChange} data-testid={testId} required={required}
              className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm bg-white focus:border-[#101876] focus:outline-none focus:ring-2 focus:ring-[#101876]/20">
        <option value="">Select...</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
function TextArea({ label, testId, ...rest }) {
  return (
    <div>
      <label className="block text-xs font-bold text-slate-700 mb-1">{label}</label>
      <textarea rows={5} {...rest} data-testid={testId}
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-[#101876] focus:outline-none focus:ring-2 focus:ring-[#101876]/20" />
    </div>
  );
}

// ============================================================
// ADMIN — Partnership Enquiries Viewer
// ============================================================

const STATUSES = ["new", "contacted", "demo_scheduled", "proposal_sent", "converted", "closed"];
const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-amber-100 text-amber-700",
  demo_scheduled: "bg-purple-100 text-purple-700",
  proposal_sent: "bg-indigo-100 text-indigo-700",
  converted: "bg-green-100 text-green-700",
  closed: "bg-slate-100 text-slate-500",
};

export function AdminPartnershipEnquiriesPage() {
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState("");
  const [orgType, setOrgType] = useState("");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (orgType) params.set("organization_type", orgType);
      if (search) params.set("search", search);
      const { data } = await api.get(`/admin/partnership-enquiries?${params}`);
      setRows(data.data?.enquiries || []);
      const { data: s } = await api.get("/admin/partnership-enquiries/summary");
      setSummary(s.data);
    } catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, orgType]);

  const setSt = async (id, st) => {
    try { await api.patch(`/admin/partnership-enquiries/${id}/status`, { status: st }); toast.success("Updated"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const saveRemarks = async (id, remarks) => {
    try { await api.put(`/admin/partnership-enquiries/${id}/remarks`, { admin_remarks: remarks }); toast.success("Saved"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this enquiry?")) return;
    try { await api.delete(`/admin/partnership-enquiries/${id}`); toast.success("Deleted"); setDetail(null); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (rows === null) return <DashboardLayout title="Partnership Enquiries"><LoadingBlock /></DashboardLayout>;

  return (
    <DashboardLayout title="Partnership Enquiries" breadcrumbs={["Admin", "Partnerships"]}>
      {summary && (
        <div className="grid gap-3 md:grid-cols-4 lg:grid-cols-7 mb-4" data-testid="partnerships-summary">
          <StatCard label="Total" value={summary.total} icon={Building2} tint="navy" />
          <StatCard label="New" value={summary.new} icon={Sparkles} tint="navy" />
          <StatCard label="Contacted" value={summary.contacted} icon={PhoneCall} tint="amber" />
          <StatCard label="Demo" value={summary.demo_scheduled} icon={Rocket} tint="navy" />
          <StatCard label="Proposal" value={summary.proposal_sent} icon={Send} tint="navy" />
          <StatCard label="Converted" value={summary.converted} icon={Check} tint="green" />
          <StatCard label="Closed" value={summary.closed} icon={X} tint="rose" />
        </div>
      )}

      <div className="mb-3 flex flex-wrap gap-2" data-testid="partnerships-filters">
        <input type="search" placeholder="Search organization / contact / email" value={search}
               onChange={(e) => setSearch(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && load()}
               data-testid="p-search"
               className="rounded-xl border border-slate-300 px-3 py-2 text-sm w-64" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="p-filter-status" className="rounded-xl border border-slate-300 px-3 py-2 text-sm">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={orgType} onChange={(e) => setOrgType(e.target.value)} data-testid="p-filter-orgtype" className="rounded-xl border border-slate-300 px-3 py-2 text-sm">
          <option value="">All org types</option>
          {ORG_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button type="button" onClick={load} className="rounded-xl bg-[#101876] px-4 py-2 text-xs font-bold text-white">Apply</button>
      </div>

      {rows.length === 0 ? <EmptyState title="No enquiries yet" description="Submissions will appear here." icon={Building2} /> :
       <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto" data-testid="partnerships-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr>
            <th className="text-left px-3 py-2">Organization</th>
            <th className="text-left px-3 py-2">Type</th>
            <th className="text-left px-3 py-2">Location</th>
            <th className="text-left px-3 py-2">Contact</th>
            <th className="text-left px-3 py-2">Interests</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-right px-3 py-2">Actions</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`p-row-${r.id}`}>
                <td className="px-3 py-2">
                  <div className="text-sm font-bold text-slate-800">{r.organization_name}</div>
                  <div className="text-[10px] text-slate-500">{new Date(r.created_at).toLocaleDateString()}</div>
                </td>
                <td className="px-3 py-2 text-xs">{r.organization_type}</td>
                <td className="px-3 py-2 text-xs">{r.city}, {r.state}</td>
                <td className="px-3 py-2 text-xs">
                  <div>{r.contact_person_name}</div>
                  <a href={`mailto:${r.email}`} className="text-slate-500 hover:text-[#101876]">{r.email}</a><br/>
                  <a href={`tel:${r.mobile}`} className="text-slate-500 hover:text-[#101876]">{r.mobile}</a>
                </td>
                <td className="px-3 py-2 text-[10px] max-w-[200px]">{(r.partnership_interests_json || []).slice(0, 3).join(", ")}</td>
                <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_COLORS[r.enquiry_status] || STATUS_COLORS.new}`}>{r.enquiry_status}</span></td>
                <td className="px-3 py-2 text-right space-x-2">
                  <button type="button" onClick={() => setDetail(r)} className="text-xs font-bold text-[#101876] hover:underline" data-testid={`p-view-${r.id}`}>View</button>
                  <button type="button" onClick={() => remove(r.id)} className="text-xs text-rose-600 hover:underline" data-testid={`p-del-${r.id}`}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>}

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDetail(null)}>
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="p-detail-modal">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-black text-slate-900">{detail.organization_name}</h2>
                <div className="text-xs text-slate-500">{detail.organization_type} · {detail.city}, {detail.state}</div>
              </div>
              <button type="button" onClick={() => setDetail(null)} className="text-slate-400 hover:text-slate-800"><X className="h-4 w-4" /></button>
            </div>
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div><dt className="text-[10px] font-bold uppercase text-slate-500">Contact</dt><dd>{detail.contact_person_name} · {detail.designation}</dd></div>
              <div><dt className="text-[10px] font-bold uppercase text-slate-500">Size</dt><dd>{detail.organization_size || "—"}</dd></div>
              <div><dt className="text-[10px] font-bold uppercase text-slate-500">Email</dt><dd><a href={`mailto:${detail.email}`} className="text-[#101876]">{detail.email}</a></dd></div>
              <div><dt className="text-[10px] font-bold uppercase text-slate-500">Mobile</dt><dd><a href={`tel:${detail.mobile}`} className="text-[#101876]">{detail.mobile}</a></dd></div>
              <div><dt className="text-[10px] font-bold uppercase text-slate-500">WhatsApp</dt><dd>{detail.whatsapp_number || "—"}</dd></div>
              <div><dt className="text-[10px] font-bold uppercase text-slate-500">Website</dt><dd>{detail.website || "—"}</dd></div>
              <div><dt className="text-[10px] font-bold uppercase text-slate-500">Learners</dt><dd>{detail.approximate_learners || "—"}</dd></div>
              <div><dt className="text-[10px] font-bold uppercase text-slate-500">Timeline</dt><dd>{detail.preferred_start_timeline || "—"}</dd></div>
            </dl>
            <div>
              <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">Interests</div>
              <div className="flex flex-wrap gap-1">
                {(detail.partnership_interests_json || []).map((i) => <span key={i} className="rounded-full bg-[#101876]/10 text-[#101876] px-2 py-0.5 text-[10px] font-bold">{i}</span>)}
              </div>
            </div>
            {detail.message && (
              <div>
                <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">Message</div>
                <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{detail.message}</div>
              </div>
            )}
            <div>
              <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Status</label>
              <select value={detail.enquiry_status} onChange={(e) => { setSt(detail.id, e.target.value); setDetail({ ...detail, enquiry_status: e.target.value }); }} data-testid="p-detail-status"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Admin remarks</label>
              <textarea rows={3} defaultValue={detail.admin_remarks || ""} data-testid="p-detail-remarks"
                        onBlur={(e) => saveRemarks(detail.id, e.target.value)}
                        placeholder="Internal notes — saved automatically on blur"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
